import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Menu, X, User, LogOut, ChevronDown, Search, ArrowRight, ShoppingCart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cartAPI, productsAPI, landingsAPI, homeSolutionsAPI } from '../lib/api';
import { getPendingCartItems, getGuestSessionId } from '../lib/pendingCart';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Input } from './ui/input';
import MeetingPopup from './MeetingPopup';
import { trackContactHeaderClick } from '../lib/metrikaGoals';
import { searchSitePages, getSearchResultLink } from '../data/siteSearchIndex';
import { navigateToCart } from '../lib/cartNavigation';

const getGuestCartCount = async () => {
  const sessionId = getGuestSessionId();
  if (!sessionId) return 0;

  try {
    const guestCart = await cartAPI.getGuestCart(sessionId);
    if (guestCart?.items) {
      return guestCart.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
    }
  } catch (e) {
    // Ignore API errors for guests
  }

  return getPendingCartItems().reduce((sum, item) => sum + (item.quantity || 1), 0);
};

const NAV_LINKS = [
  { label: 'Компания', children: [{ path: '/', label: 'Главная' }, { path: '/about', label: 'О компании' }] },
  {
    label: 'Каталог',
    children: [
      { path: '/solutions', label: 'Каталог решений' },
      { path: '/gen-ai-creative', label: 'Gen AI', description: 'Инфраструктура для команд' },
      { path: '/services', label: 'ИИ-решения', description: 'Агенты, RAG, on-prem' },
      { path: '/enterprise-systems', label: 'Внедрение корпоративных систем', description: 'Поставка и внедрение ПО' },
      { path: '/cbr', label: 'Поддержка AI', description: 'Центр быстрого реагирования' },
    ],
  },
  {
    label: 'Блог',
    children: [
      { path: '/novosti-antologia', label: 'Блог' },
      { path: '/ready-for-ai', label: 'Чек-лист готовности к AI' },
      { path: '/ready-for-ai-sales', label: 'Чек-лист для продаж' },
    ],
  },
];

function buildMobileNavItems(links) {
  const items = [];
  links.forEach((link) => {
    if (link.children?.length) {
      items.push({ type: 'group', label: link.label });
      link.children.forEach((child) => {
        items.push({
          type: 'link',
          path: child.path,
          label: child.label,
          description: child.description,
        });
      });
    } else if (link.path) {
      items.push({
        type: 'link',
        path: link.path,
        label: link.label,
        description: link.description,
      });
    }
  });
  return items;
}

const MOBILE_NAV_ITEMS = buildMobileNavItems(NAV_LINKS);

const Header = ({
  logoSrc = '/logo.svg',
  logoAlt = 'Антология',
  logoClassName,
  tallLogo = false,
  onContactClick,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [isWorking, setIsWorking] = useState(true);
  const [workingStatusLabel, setWorkingStatusLabel] = useState(
    'Работаем до 19:00 по Мск',
  );
  const [isMeetingPopupOpen, setIsMeetingPopupOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchExtras, setSearchExtras] = useState({
    landings: [],
    homeSolutions: [],
    loaded: false,
  });
  const [searchProductHits, setSearchProductHits] = useState([]);
  const searchContainerRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const { isAuthenticated, user, logout } = useAuth();

  const goToCart = () => navigateToCart(navigate);

  // Функция для определения статуса работы по московскому времени
  const checkWorkingStatus = () => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Europe/Moscow',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const weekday = parts.find((part) => part.type === 'weekday')?.value;
    const hours = Number(parts.find((part) => part.type === 'hour')?.value);
    const minutes = Number(parts.find((part) => part.type === 'minute')?.value);

    // Сб–Вс — выходной
    if (weekday === 'Sat' || weekday === 'Sun') {
      return { isWorking: false, label: 'Выходной' };
    }

    const currentMinutes = hours * 60 + minutes;
    // Работаем с 10:00 до 19:00 МСК
    const workStart = 10 * 60;
    const workEnd = 19 * 60;
    const isWorkingNow = currentMinutes >= workStart && currentMinutes < workEnd;

    return {
      isWorking: isWorkingNow,
      label: isWorkingNow
        ? 'Работаем до 19:00 по Мск'
        : 'Отдыхаем до 10:00 по Мск',
    };
  };

  // Обновляем статус работы
  useEffect(() => {
    const updateStatus = () => {
      const status = checkWorkingStatus();
      setIsWorking(status.isWorking);
      setWorkingStatusLabel(status.label);
    };
    
    // Обновляем сразу
    updateStatus();
    
    // Обновляем каждую минуту
    const interval = setInterval(updateStatus, 60000);
    
    return () => clearInterval(interval);
  }, []);

  // Корзина не стартует вместе с ручками каталога: счётчик догружается в idle.
  useEffect(() => {
    let cancelled = false;
    let idleId = 0;
    let timeoutId = 0;

    const loadCart = async () => {
      try {
        let count = 0;
        
        if (isAuthenticated) {
          const cart = await cartAPI.getCart();
          count = Array.isArray(cart) ? cart.length : 0;
        } else {
          count = await getGuestCartCount();
        }
        
        if (cancelled) return;
        setCartCount(count);
      } catch (error) {
        console.error('Error loading cart:', error);
        if (!cancelled) setCartCount(0);
      }
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(() => {
        if (!cancelled) loadCart();
      }, { timeout: 2000 });
    } else {
      timeoutId = window.setTimeout(() => {
        if (!cancelled) loadCart();
      }, 1);
    }
    
    const handleStorageChange = () => {
      loadCart();
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cart-updated', handleStorageChange);
    
    return () => {
      cancelled = true;
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) window.clearTimeout(timeoutId);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cart-updated', handleStorageChange);
    };
  }, [isAuthenticated, location.pathname]);

  // Facets for header search (landings / home) — never full catalog.
  const loadSearchExtras = async () => {
    if (searchExtras.loaded) return;
    try {
      const [landingsData, homeSolutionsData] = await Promise.all([
        landingsAPI.getActiveLandings().catch(() => []),
        homeSolutionsAPI.getActive().catch(() => []),
      ]);

      const complexSolutions = (Array.isArray(landingsData) ? landingsData : []).map((landing) => ({
        id: landing.id,
        name: landing.title || 'Комплексное решение',
        short_description: landing.hero_description || landing.meta_description || landing.title || '',
        tags: landing.tags || [],
        slug: landing.slug,
      }));

      const homepageSolutions = (Array.isArray(homeSolutionsData) ? homeSolutionsData : []).map((item) => ({
        id: item.id,
        name: item.title,
        short_description: item.description || item.title,
        tags: item.tags || [],
        slug: undefined,
        link: item.link,
      }));

      setSearchExtras({
        landings: complexSolutions,
        homeSolutions: homepageSolutions,
        loaded: true,
      });
    } catch (error) {
      console.error('Error loading search extras:', error);
    }
  };

  // Debounced catalog suggest: view=catalog&q=&limit=8
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchProductHits([]);
      return undefined;
    }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    let cancelled = false;
    searchDebounceRef.current = setTimeout(() => {
      productsAPI
        .getProducts({ view: 'catalog', q, limit: 8, offset: 0 })
        .then((page) => {
          if (cancelled) return;
          const items = Array.isArray(page) ? page : page?.items || [];
          setSearchProductHits(items);
        })
        .catch(() => {
          if (!cancelled) setSearchProductHits([]);
        });
    }, 300);
    return () => {
      cancelled = true;
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery]);

  // Результаты поиска: страницы + локальные extras + серверный suggest
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    const pageResults = searchSitePages(query)
      .slice(0, 3)
      .map((page) => ({ ...page, kind: 'page' }));

    const localExtras = [...searchExtras.homeSolutions, ...searchExtras.landings]
      .filter((product) => {
        return (
          product.name?.toLowerCase().includes(query) ||
          product.short_description?.toLowerCase().includes(query) ||
          (product.tags && product.tags.some((tag) => tag.toLowerCase().includes(query)))
        );
      })
      .slice(0, 3)
      .map((product) => ({ ...product, kind: 'product' }));

    const seen = new Set(localExtras.map((p) => p.id));
    const apiHits = searchProductHits
      .filter((p) => p?.id && !seen.has(p.id))
      .map((product) => ({ ...product, kind: 'product' }));

    return [...pageResults, ...localExtras, ...apiHits].slice(0, 8);
  }, [searchQuery, searchExtras, searchProductHits]);

  // Закрытие выпадающего списка при клике вне его
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50 overflow-x-clip">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-5 lg:px-6 xl:px-8">
        <div
          className={`flex items-center justify-between gap-2 lg:gap-3 ${
            tallLogo ? 'h-20' : 'h-16'
          }`}
        >
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center group">
            <img
              src={logoSrc}
              alt={logoAlt}
              className={
                logoClassName ||
                'h-10 w-auto transition-transform group-hover:scale-105'
              }
            />
          </Link>

          {/* Desktop Navigation — от lg (1024px): landscape iPhone ~926px остаётся на mobile-меню */}
          <nav className="hidden lg:flex items-center gap-2 xl:gap-3 flex-1 min-w-0 mx-2 lg:mx-3 xl:mx-4">
            {NAV_LINKS.map((link) => (
              link.children ? (
                <DropdownMenu key={link.label}>
                  <DropdownMenuTrigger asChild>
                    <button className="flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium transition-colors hover:text-accent text-muted-foreground cursor-pointer">
                      {link.label}
                      <ChevronDown className="w-4 h-4 shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-72 min-w-[18rem] p-2">
                    {link.children.map((child) => {
                      const isActive =
                        location.pathname === child.path ||
                        (child.path !== '/' && location.pathname.startsWith(`${child.path}/`));
                      return (
                      <DropdownMenuItem
                        key={child.path}
                        asChild
                        className="p-0 rounded-lg text-base focus:bg-transparent data-[highlighted]:bg-transparent !cursor-pointer"
                      >
                        <Link
                          to={child.path}
                          className={`nav-dropdown-link flex flex-col items-center text-center gap-1 w-full py-3.5 px-4 rounded-lg text-base font-medium transition-colors !cursor-pointer ${
                            isActive ? 'text-accent' : 'text-foreground'
                          } data-[highlighted]:text-accent [&[data-highlighted]_.nav-menu-desc]:text-accent/80`}
                        >
                          <span>{child.label}</span>
                          {child.description && (
                            <span className="nav-menu-desc text-sm font-normal text-muted-foreground leading-snug">
                              {child.description}
                            </span>
                          )}
                        </Link>
                      </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link key={link.path} to={link.path} className={`shrink-0 whitespace-nowrap text-sm font-medium transition-colors hover:text-accent cursor-pointer ${location.pathname === link.path ? 'text-accent' : 'text-muted-foreground'}`}>
                  {link.label}
                </Link>
              )
            ))}
             
            {/* Search Bar */}
            <div ref={searchContainerRef} className="relative hidden xl:block flex-1 min-w-[100px] max-w-[260px] 2xl:max-w-[320px] overflow-visible z-20">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
              <Input
                type="text"
                placeholder="Поиск по сайту..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSearchResults(true);
                  loadSearchExtras();
                }}
                onFocus={() => {
                  setSearchFocused(true);
                  loadSearchExtras();
                  if (searchQuery.trim() && searchResults.length > 0) {
                    setShowSearchResults(true);
                  }
                }}
                onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                className={`pl-9 h-9 w-full text-sm bg-white border-border focus:border-accent rounded-lg placeholder:opacity-50 transition-all duration-200 ${searchFocused ? '2xl:w-[180%] 2xl:shadow-lg' : ''}`}
              />
              
              {/* Выпадающий список результатов поиска */}
              {showSearchResults && searchQuery.trim() && searchResults.length > 0 && (
                <div className="absolute top-full left-0 w-[600px] mt-2 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[600px] overflow-y-auto">
                  <div className="p-3">
                    {searchResults.map((item) => (
                      <Link
                        key={item.kind === 'page' ? item.id : item.id}
                        to={getSearchResultLink(item)}
                        onClick={() => {
                          setShowSearchResults(false);
                          setSearchQuery('');
                        }}
                        className="flex items-start gap-3 p-3 rounded-lg hover:bg-accent/5 transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-sm group-hover:text-accent transition-colors">
                              {item.name}
                            </h4>
                            {item.kind === 'page' && (
                              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                Страница
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {item.matchContext || item.short_description}
                          </p>
                          {item.kind === 'product' && item.tags && item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {item.tags.slice(0, 3).map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="text-xs px-2 py-0.5 bg-accent/10 text-accent rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Индикатор статуса работы — скрываем первым на узких desktop-экранах */}
            <div className="hidden xl:flex shrink-0 items-center gap-2 px-3 py-1.5 rounded-full bg-card/50 border border-border/50">
              <div 
                className={`w-2 h-2 rounded-full ${
                  isWorking 
                    ? 'bg-green-500 animate-pulse' 
                    : 'bg-red-500 animate-pulse'
                }`}
              />
              <span className="text-xs font-medium text-muted-foreground">
                {workingStatusLabel}
              </span>
            </div>
          </nav>

          {/* CTA Button and Auth */}
          <div className="hidden lg:flex shrink-0 items-center gap-2 xl:gap-3">
            {isAuthenticated ? (
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <User className="w-4 h-4" />
                      <span className="max-w-[150px] truncate">{user?.email}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Мой аккаунт</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                      Личный кабинет
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); navigate('/'); }}>
                      <LogOut className="w-4 h-4 mr-2" />
                      Выйти
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                {cartCount > 0 && (
                  <Badge 
                    variant="default" 
                    className="absolute -top-2 -right-2 h-5 min-w-5 px-1.5 flex items-center justify-center text-xs font-bold bg-accent text-accent-foreground rounded-full border-2 border-background shadow-lg z-10"
                  >
                    {cartCount}
                  </Badge>
                )}
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate('/login')}
                className="font-medium whitespace-nowrap px-2 xl:px-4"
              >
                <span className="hidden xl:inline">Вход в ЛК</span>
                <User className="w-4 h-4 xl:hidden" aria-hidden="true" />
                <span className="sr-only xl:hidden">Вход в ЛК</span>
              </Button>
            )}
            <Button
              onClick={() => {
                trackContactHeaderClick();
                if (onContactClick) {
                  onContactClick();
                  return;
                }
                setIsMeetingPopupOpen(true);
              }}
              className="bg-[#F08517] hover:bg-accent text-white font-medium whitespace-nowrap px-3 xl:px-4"
            >
              Связаться
            </Button>
            <Button
              variant="outline"
              onClick={goToCart}
              className="relative font-medium"
            >
              <ShoppingCart className="w-4 h-4" />
              {cartCount > 0 && (
                <Badge 
                  variant="default" 
                  className="absolute -top-2 -right-2 h-5 min-w-5 px-1 flex items-center justify-center text-xs font-bold bg-accent text-accent-foreground rounded-full"
                >
                  {cartCount}
                </Badge>
              )}
            </Button>
          </div>

          {/* Mobile: корзина + меню (portrait и landscape до lg) */}
          <div className="flex lg:hidden items-center gap-1 sm:gap-2 shrink-0">
            <Button
              variant="outline"
              size="icon"
              onClick={goToCart}
              className="relative h-9 w-9"
              aria-label="Корзина"
            >
              <ShoppingCart className="w-4 h-4" />
              {cartCount > 0 && (
                <Badge
                  variant="default"
                  className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 flex items-center justify-center text-[10px] font-bold bg-accent text-accent-foreground rounded-full"
                >
                  {cartCount}
                </Badge>
              )}
            </Button>
            <button
              type="button"
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border/50 animate-fade-in max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain">
            <nav className="flex flex-col gap-2">
              {/* Индикатор статуса работы для мобильных */}
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-card/50 border border-border/50 mb-2">
                <div 
                  className={`w-2 h-2 rounded-full ${
                    isWorking 
                      ? 'bg-green-500 animate-pulse' 
                      : 'bg-red-500 animate-pulse'
                  }`}
                />
                <span className="text-xs font-medium text-muted-foreground">
                  {workingStatusLabel}
                </span>
              </div>

              {/* Mobile Search Bar */}
              <div className="relative px-4 mb-2 overflow-visible">
                <Search className="absolute left-7 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground z-10" />
                <Input
                  type="text"
                  placeholder="Поиск по сайту..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchResults(true);
                    loadSearchExtras();
                  }}
                  onFocus={() => {
                    setSearchFocused(true);
                    loadSearchExtras();
                    if (searchQuery.trim() && searchResults.length > 0) {
                      setShowSearchResults(true);
                    }
                  }}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  className="pl-10 h-10 w-full text-sm bg-white border-border focus:border-accent rounded-lg placeholder:opacity-50"
                />
                
                {/* Выпадающий список результатов поиска для мобильных */}
                {showSearchResults && searchQuery.trim() && searchResults.length > 0 && (
                  <div className="absolute top-full left-4 right-4 mt-2 bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                    <div className="p-2">
                      {searchResults.map((item) => (
                        <Link
                          key={item.kind === 'page' ? item.id : item.id}
                          to={getSearchResultLink(item)}
                          onClick={() => {
                            setShowSearchResults(false);
                            setSearchQuery('');
                            setIsMenuOpen(false);
                          }}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-accent/5 transition-colors group"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-sm group-hover:text-accent transition-colors">
                                {item.name}
                              </h4>
                              {item.kind === 'page' && (
                                <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                  Страница
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {item.matchContext || item.short_description}
                            </p>
                            {item.kind === 'product' && item.tags && item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {item.tags.slice(0, 3).map((tag, idx) => (
                                  <span
                                    key={idx}
                                    className="text-xs px-2 py-0.5 bg-accent/10 text-accent rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {MOBILE_NAV_ITEMS.map((item, index) =>
                item.type === 'group' ? (
                  <div
                    key={`group-${item.label}`}
                    className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                  >
                    {item.label}
                  </div>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMenuOpen(false)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors cursor-pointer ${
                      location.pathname === item.path ||
                      (item.path !== '/' && location.pathname.startsWith(`${item.path}/`))
                        ? 'bg-accent/10 text-accent'
                        : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <span className="font-medium block">{item.label}</span>
                    {item.description && (
                      <span className="text-xs font-normal text-muted-foreground mt-0.5 block">
                        {item.description}
                      </span>
                    )}
                  </Link>
                )
              )}
              
              {isAuthenticated ? (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMenuOpen(false)}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted"
                  >
                    Личный кабинет
                  </Link>
                  <button
                    onClick={() => { logout(); setIsMenuOpen(false); navigate('/'); }}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted text-left"
                  >
                    Выйти
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted"
                >
                  Вход в ЛК
                </Link>
              )}
              <button
                type="button"
                onClick={() => {
                  goToCart();
                  setIsMenuOpen(false);
                }}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors text-muted-foreground hover:bg-muted text-left flex items-center gap-2"
              >
                <ShoppingCart className="w-4 h-4" />
                <span>Корзина</span>
                {cartCount > 0 && (
                  <Badge
                    variant="default"
                    className="h-5 min-w-5 px-1.5 flex items-center justify-center text-xs font-bold bg-accent text-accent-foreground rounded-full"
                  >
                    {cartCount}
                  </Badge>
                )}
              </button>
              <Button
                onClick={() => {
                  trackContactHeaderClick();
                  setIsMenuOpen(false);
                  if (onContactClick) {
                    onContactClick();
                    return;
                  }
                  setIsMeetingPopupOpen(true);
                }}
                className="w-full mt-2 bg-[#F08517] hover:bg-accent text-white font-medium"
              >
                Связаться
              </Button>
            </nav>
          </div>
        )}
      </div>

      {/* Meeting Popup */}
      <MeetingPopup open={isMeetingPopupOpen} onOpenChange={setIsMeetingPopupOpen} />
    </header>
  );
};

export default Header;
